import 'select2';
import 'select2/dist/css/select2.css';

import { gql, ApolloProvider } from '@apollo/client';
import $ from 'jquery';
import { first, isNull } from 'lodash-es';
import { useObservableState } from 'observable-hooks';
import PropTypes from 'prop-types';
import { createRoot } from 'react-dom/client';
import {
    BehaviorSubject,
    combineLatest,
    firstValueFrom,
    map,
    skip,
    skipWhile,
    tap,
    throttleTime,
} from 'rxjs';
import DataSetDateRangePicker from './graph/dataset-date-range-picker';

import { MultiSensorDataSet } from './graph/dataset';
import { ValueFrequencyGraph, ValueTimeGraph } from './graph/graph';
import {
    LastSeenPoller,
    Scales,
    initLineWarningHelper,
    setGraphLoadedFlag,
    setLogo,
    overrides$,
    dataSetGuideLine,
} from './graph/graph-helpers';
import { XAxisScaleSlider, YAxisScaleSlider } from './graph/scale-slider';
import VeffDayGraph from './graph/veff-day-graph';
import { getClient } from './utils/apollo-client';
import { applyNamingConventionsByGuideline, defaultGuideline } from './utils/naming-conventions';
import { loggedIn$ } from './graph/http-helper';
import { VperTable } from './graph/vper-table';
import { DATA_TYPE, ScaleType } from './graph/enums';
import { LegendData } from './graph/legend-helpers';
import ExportButtons from './components/graph/components/ExportButtons';
import { ScaleTypeSelect } from './graph/axis';
import OverridesForm from './components/graph/OverridesForm';
import { NotificationsProvider } from './components/notifications/Notifications';
import globalProfile, { ProfileProvider } from './profile';
import DataTabSelector from './components/graph/dataTab/DataTabSelector';
import DataTabContent from './components/graph/dataTab/DataTabContent';
import MeasuringPointSelect from './components/measuringPointSelect/MeasuringPointSelect';
import { RelatedPagesButtons } from './components/graph/RelatedPagesButtons';
import { GraphToolbar } from './components/graph/components/GraphToolbar';
import { GraphTimeFilters } from './components/graph/components/graphTimeFilters/GraphTimeFilters';
import { getInitialSbrDateRange } from './sbr-page-date-range';
import { loadSbrMeasuringPoint } from './sbr-page-measuring-point-loader';

// Helper method that can be executed by dev's to get the PDF view URL
// of the current graph page they are looking at.
window.createPdfViewUrl = async () => {
    const dataSet = window.timeGraph.data;

    const [dataType, mpId, dateRange] = await firstValueFrom(
        combineLatest([dataSet.dataType$, dataSet.sensor$, dataSet.dateRange.observable$])
    );

    return [`/sbr/pdf_view`, mpId, dateRange[0], dateRange[1], dataType].join('/');
};

function ToggleableDateRangePicker({ visible$, dataSet, language }) {
    const showPicker = useObservableState(visible$, false);

    if (!showPicker) {
        return null;
    }

    return <DataSetDateRangePicker dataSet={dataSet} language={language} />;
}
ToggleableDateRangePicker.propTypes = {
    dataSet: PropTypes.object.isRequired,
    language: PropTypes.string.isRequired,
    visible$: PropTypes.object.isRequired,
};

function sbrPage(module) {
    const {
        dateRangePickerLanguage,
        logos,
        lastMeasuringPoint,
        lastStart,
        lastEnd,
        lastOnlineUrl,
        getAlarmsUrl,
        getShowTracesUrl,
        initialGuidelines,
    } = module.options;

    const height = 550;
    const width = $('.graph-area')[0].clientWidth - 70;
    const barGraphMargin = {
        top: 20,
        right: 0,
        bottom: 120,
        left: 55,
    };

    const dotGraphMargin = {
        top: 20,
        right: 0,
        bottom: 40,
        left: 55,
    };

    const dataSet = new MultiSensorDataSet(getAlarmsUrl);
    function getSelectedDataTypeFromUrl() {
        const incomingDataType = new URLSearchParams(window.location.search).get('dataType');

        return Object.values(DATA_TYPE).includes(incomingDataType) ? incomingDataType : null;
    }
    const selectedDataTypeFromUrl = getSelectedDataTypeFromUrl();

    const showDateRangePicker$ = new BehaviorSubject(false);
    createRoot(document.getElementById('date-range-picker-placeholder')).render(
        <ToggleableDateRangePicker
            visible$={showDateRangePicker$}
            dataSet={dataSet}
            language={dateRangePickerLanguage}
        />
    );

    // Init MP selector.
    (() => {
        const mpSelectorId = 'measuring-point-selector';

        function loadMp(id, initialDataType = null) {
            loadSbrMeasuringPoint({ id, mpSelectorId, dataSet, initialDataType });
        }

        function onReady() {
            const mpSelector = $(`select#${mpSelectorId}`);
            let mpId = null;

            if (window.location.hash) {
                mpId = window.location.hash.replace('#', '');
            } else if (lastMeasuringPoint && lastMeasuringPoint.length) {
                // For now we can only select one, so always pick the first.
                mpId = first(lastMeasuringPoint);
            } else {
                mpId = $(`#${mpSelectorId} option:nth-child(1)`).attr('value');
            }

            // Only select if we have something to select.
            if (mpId) {
                mpSelector.val(mpId);
                // Trigger the `change.select2` event to update the displayed label.
                mpSelector.trigger('change.select2');

                loadMp(mpId, selectedDataTypeFromUrl);
            }
        }

        createRoot(document.getElementById('measuring-point-selector-wrapper')).render(
            <ApolloProvider client={getClient()}>
                <MeasuringPointSelect
                    selectId={mpSelectorId}
                    onSelectChange={loadMp}
                    onReady={onReady}
                />
            </ApolloProvider>
        );
    })();

    const initialDateRange = getInitialSbrDateRange({
        search: window.location.search,
        lastStart,
        lastEnd,
    });
    if (initialDateRange) {
        dataSet.setVisibleMinMax(new Date(initialDateRange.start), new Date(initialDateRange.end));
    }
    const scales = new Scales(globalProfile);
    const legendData = new LegendData();

    const baseConfig = {
        height,
        width,
        scales,
        legendData,
        getShowTracesUrl,
        profile: globalProfile,
    };

    const timeGraph = new ValueTimeGraph({
        ...baseConfig,
        margin: barGraphMargin,
        name: 'bar',
        container: '#graph-velocity',
        data: dataSet,
        showBrushGraph: true,
        animate: true,
    });

    window.timeGraph = timeGraph;

    // Pulled this observable apart so that it can be controlled by the toggle button too.
    const frequencyGraphScaleType$ = new BehaviorSubject(ScaleType.Auto);

    const frequencyGraph = new ValueFrequencyGraph({
        ...baseConfig,
        margin: dotGraphMargin,
        name: 'dot',
        container: '#graph-dot',
        data: dataSet,
        dotSize: 4,
        scaleType$: frequencyGraphScaleType$,
    });

    window.frequencyGraph = frequencyGraph;

    // Show/hide line warnings.
    initLineWarningHelper(frequencyGraph.$container, dataSet);

    const veffDayGraph = new VeffDayGraph({
        ...baseConfig,
        margin: dotGraphMargin,
        name: 'veffDay',
        container: '#graph-veff-day',
        data: dataSet,
        timeGraph,
        dotSize: 4,
    });

    const graphs = [timeGraph, frequencyGraph, veffDayGraph];

    // eslint-disable-next-line no-new
    new VperTable('#vss-table', dataSet, legendData);

    // eslint-disable-next-line no-new
    new LastSeenPoller(lastOnlineUrl, dataSet);

    createRoot(document.getElementById('data-type-selector')).render(
        <DataTabSelector dataSet={dataSet} />
    );

    createRoot(document.getElementById('data-type-content')).render(
        <ProfileProvider profile={globalProfile}>
            <DataTabContent
                dataSet={dataSet}
                graphElement={document.getElementById('graph-velocity')}
            />
        </ProfileProvider>
    );

    // Set graph logo.
    dataSet.currentSensor$.subscribe((id) => {
        if (isNull(id)) {
            // ID can be `null` while initializing.
            return;
        }

        const logo = logos[id.toString()];

        graphs.forEach((graph) => {
            setLogo(graph.graph, logo);
        });
    });

    // Init naming conventions changer.
    (() => {
        let initialGuideLine = defaultGuideline;
        if (window.location.hash) {
            const mpId = parseInt(window.location.hash.replace('#', ''), 10);
            // Only not-DUST measuring points have guidelines.
            if (Object.keys(initialGuidelines).includes(mpId)) {
                initialGuideLine = initialGuidelines[mpId];
            }
        }

        dataSetGuideLine(dataSet, initialGuideLine).subscribe((guideLine) => {
            applyNamingConventionsByGuideline(guideLine);
        });
    })();

    // Init range sliders.
    (() => {
        // Create Y axis range sliders.
        graphs.forEach((graph) => {
            // eslint-disable-next-line no-new
            new YAxisScaleSlider(graph, scales);
        });

        // Create one X axis range slider for the Fdom/Vtop graph.
        // eslint-disable-next-line no-new
        new XAxisScaleSlider(frequencyGraph, scales);
    })();

    // Init exports.
    (() => {
        timeGraph.ready.then(() => {
            createRoot(document.getElementById('export-choices')).render(
                <ApolloProvider client={getClient()}>
                    <ProfileProvider profile={globalProfile}>
                        <ExportButtons
                            dataSet={dataSet}
                            graphType$={timeGraph.graphType$}
                            scales={scales}
                            legendData={legendData}
                            frequencyGraphScaleType$={frequencyGraphScaleType$}
                        />
                    </ProfileProvider>
                </ApolloProvider>
            );
        });
    })();

    // Init related pages buttons.
    createRoot(document.getElementById('related-pages-buttons')).render(
        <RelatedPagesButtons
            dataSet={dataSet}
            overrides$={overrides$}
            getShowTracesUrl={getShowTracesUrl}
        />
    );

    // Store last graph state.
    {
        const storeGraphStateMutation = gql`
            mutation storeGraphState(
                $measuringPointId: Int!
                $startTime: BigInteger!
                $endTime: BigInteger!
            ) {
                storeGraphState(
                    measuringPointId: $measuringPointId
                    startTime: $startTime
                    endTime: $endTime
                ) {
                    ok
                }
            }
        `;

        combineLatest([
            dataSet.dateRange.observable$.pipe(
                // Leading is `false` because we don't want to send a mutation
                // when someone starts dragging, only at the end of dragging
                // or after 300ms while dragging.
                throttleTime(300, null, { leading: false, trailing: true })
            ),
            dataSet.currentSensor$,
        ])
            .pipe(
                // Skip the first emission as that just the currently loaded
                // state, we only want emissions that are triggered by an update.
                skip(1),
                map(([[min, max], currentSensor]) => ({
                    measuringPointId: Number(currentSensor),
                    startTime: min,
                    endTime: max,
                })),
                // Only continue when the measuringPointId has a value.
                skipWhile((state) => !state.measuringPointId),
                tap(async (state) => {
                    await getClient().mutate({
                        variables: state,
                        mutation: storeGraphStateMutation,
                    });
                })
            )
            .subscribe();
    }

    // Init frequency graph scale type toggle button.
    function initFrequencyGraphScaleTypeToggleButton() {
        const id = 'frequency-scale-type-select';

        frequencyGraph.ready.then(() => {
            createRoot(document.getElementById('toggle-scale-type')).render(
                <span className="relative -top-[10px] inline-block">
                    <label htmlFor={id} className="mr-1">
                        {gettext('SCALE_TYPE_LABEL')}
                    </label>
                    <div className="inline-block">
                        <ScaleTypeSelect scaleTypeObservable$={frequencyGraphScaleType$} id={id} />
                    </div>
                </span>
            );
        });
    }

    initFrequencyGraphScaleTypeToggleButton();

    [
        {
            elementId: 'graph-velocity-buttons',
            graph: timeGraph,
            render: (graph) => (
                <GraphToolbar graph={graph} showCursorFunctions={true} showGraphType={true} />
            ),
        },
        {
            elementId: 'graph-velocity-filters',
            graph: timeGraph,
            render: (graph) => (
                <GraphTimeFilters
                    dataSet={dataSet}
                    graph={graph}
                    showDateRangePicker$={showDateRangePicker$}
                />
            ),
        },
        {
            elementId: 'graph-dot-buttons',
            graph: frequencyGraph,
            render: (graph) => <GraphToolbar graph={graph} />,
        },
        {
            elementId: 'graph-veff-day-buttons',
            graph: veffDayGraph,
            render: (graph) => <GraphToolbar graph={graph} />,
        },
    ].forEach(({ elementId, graph, render }) => {
        graph.ready.then(() => {
            createRoot(document.getElementById(elementId)).render(render(graph));
        });
    });

    createRoot(document.getElementById('overrides-container')).render(
        <ApolloProvider client={getClient()}>
            <ProfileProvider profile={globalProfile}>
                <NotificationsProvider>
                    <OverridesForm
                        dataSet={dataSet}
                        setOverrides={(overrides) => {
                            overrides$.next(overrides);
                        }}
                    />
                </NotificationsProvider>
            </ProfileProvider>
        </ApolloProvider>
    );

    // Refresh the page when it is detected that the user is logged out, this
    // will show the login page.
    loggedIn$.subscribe((loggedIn) => !loggedIn && window.location.reload());

    // Let Chromium know we are ready.
    setGraphLoadedFlag(dataSet.ready);
}

export default sbrPage;
